import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination,
  FilterBar, FilterField, Input, RadixSelect, type DataTableColumn, type RowActionItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useNoticesQuery, useNoticeMutation } from '../hooks/useNoticesQuery';
import type { Notice, NoticeListQuery, NoticeStatus } from '../types/notice';

const PAGE_SIZE = 20;
const ALL_STATUS = '';

interface AppliedFilters {
  status: string;
  category: string;
  page: number;
}

const INITIAL_FILTERS: AppliedFilters = { status: ALL_STATUS, category: '', page: 1 };

const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('ko-KR') : '-';

interface ReorderButtonProps {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

/** 고정 공지 순서 이동용 작은 아이콘 버튼. 비활성(맨위/맨아래)일 때 시각적으로 흐려진다. */
const ReorderButton = ({ label, disabled, onClick, children }: ReorderButtonProps) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-30"
    style={{
      color: 'var(--content-default)',
      border: '1px solid var(--divider-strong)',
      background: 'var(--surface-0)',
    }}
  >
    {children}
  </button>
);

export const NoticeListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [draftStatus, setDraftStatus] = useState(ALL_STATUS);
  const [draftCategory, setDraftCategory] = useState('');
  const [applied, setApplied] = useState<AppliedFilters>(INITIAL_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState<Notice | null>(null);

  const listQuery: NoticeListQuery = useMemo(
    () => ({
      page: applied.page,
      size: PAGE_SIZE,
      ...(applied.status ? { status: applied.status as NoticeStatus } : {}),
      ...(applied.category ? { category: applied.category } : {}),
    }),
    [applied],
  );

  const query = useNoticesQuery(listQuery);
  const { remove, publish, reorder } = useNoticeMutation();

  const items = query.data?.items ?? [];
  const pagination = query.data?.pagination;

  // 현재 페이지에서 고정(isPinned)된 공지의 노출 순서. BE 가 isPinned DESC, pinOrder ASC 로
  // 정렬하므로 items 순서를 그대로 신뢰한다. up/down 은 인접 고정공지와 자리를 바꾼다.
  const pinnedIds = useMemo(
    () => items.filter((n) => n.isPinned).map((n) => n.noticeId),
    [items],
  );

  // noticeId → 고정공지 시퀀스 내 0-base 인덱스(고정 아니면 -1).
  const pinnedIndexOf = (noticeId: number) => pinnedIds.indexOf(noticeId);

  // row 와 인접 고정공지의 자리를 바꾼 새 순서 배열을 만들어 일괄 reorder 엔드포인트로 전송한다
  // (be-catalog 계약: PUT /reorder { orderedNoticeIds }, 배열 인덱스=pinOrder).
  const swapPinOrder = (row: Notice, direction: -1 | 1) => {
    const idx = pinnedIndexOf(row.noticeId);
    const neighborIdx = idx + direction;
    if (idx < 0 || neighborIdx < 0 || neighborIdx >= pinnedIds.length) return;
    const rowId = pinnedIds[idx];
    const neighborId = pinnedIds[neighborIdx];
    if (rowId === undefined || neighborId === undefined) return;
    const next = pinnedIds.map((id) => {
      if (id === rowId) return neighborId;
      if (id === neighborId) return rowId;
      return id;
    });
    reorder.mutate(next);
  };

  const statusOptions = useMemo(
    () => [
      { value: ALL_STATUS, label: t('notice.filter.statusAll') },
      { value: 'DRAFT', label: t('notice.status.DRAFT') },
      { value: 'PUBLISHED', label: t('notice.status.PUBLISHED') },
    ],
    [t],
  );

  const applyFilters = () =>
    setApplied({ status: draftStatus, category: draftCategory.trim(), page: 1 });

  const resetFilters = () => {
    setDraftStatus(ALL_STATUS);
    setDraftCategory('');
    setApplied(INITIAL_FILTERS);
  };

  const columns: DataTableColumn<Notice>[] = [
    {
      key: 'title',
      header: t('notice.cols.title'),
      cell: (row) => (
        <span
          className="block max-w-[420px] truncate text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
          title={row.title}
        >
          {row.title}
        </span>
      ),
    },
    {
      key: 'category',
      header: t('notice.cols.category'),
      width: 140,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {row.category ?? '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('notice.cols.status'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <StatusBadge
          variant={row.status === 'PUBLISHED' ? 'success' : 'neutral'}
          label={row.status ? t(`notice.status.${row.status}`, { defaultValue: '-' }) : '-'}
        />
      ),
    },
    {
      key: 'isPinned',
      header: t('notice.cols.isPinned'),
      align: 'center',
      width: 140,
      cell: (row) => {
        if (!row.isPinned) {
          return (
            <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
              -
            </span>
          );
        }
        const idx = pinnedIndexOf(row.noticeId);
        const isFirst = idx <= 0;
        const isLast = idx === pinnedIds.length - 1;
        return (
          <div className="flex items-center justify-center gap-1.5">
            <StatusBadge variant="info" label={t('notice.pinned')} />
            <div className="flex items-center gap-0.5">
              <ReorderButton
                label={t('notice.pinMoveUp')}
                disabled={isFirst || reorder.isPending}
                onClick={() => swapPinOrder(row, -1)}
              >
                <ChevronUp size={14} />
              </ReorderButton>
              <ReorderButton
                label={t('notice.pinMoveDown')}
                disabled={isLast || reorder.isPending}
                onClick={() => swapPinOrder(row, 1)}
              >
                <ChevronDown size={14} />
              </ReorderButton>
            </div>
          </div>
        );
      },
    },
    {
      key: 'publishedAt',
      header: t('notice.cols.publishedAt'),
      width: 180,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.publishedAt)}
        </span>
      ),
    },
    {
      key: 'viewCount',
      header: t('notice.cols.viewCount'),
      align: 'right',
      width: 96,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.viewCount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => {
        const actions: RowActionItem[] = [
          { key: 'edit', label: t('notice.edit'), onClick: () => navigate(`/cms/notices/${row.noticeId}`) },
          ...(row.status === 'DRAFT'
            ? [{ key: 'publish', label: t('notice.publish'), onClick: () => publish.mutate(row.noticeId) }]
            : []),
          {
            key: 'delete',
            label: t('notice.delete'),
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel={t('notice.rowActionsLabel')} actions={actions} />;
      },
    },
  ];

  return (
    <PageShell
      title={t('notice.title')}
      actions={
        <Link to="/cms/notices/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('notice.create')}
          </Button>
        </Link>
      }
    >
      <FilterBar
        className="mb-4"
        filters={
          <>
            <FilterField label={t('notice.filter.status')}>
              <RadixSelect
                value={draftStatus}
                onChange={setDraftStatus}
                options={statusOptions}
                aria-label={t('notice.filter.status')}
              />
            </FilterField>
            <FilterField label={t('notice.filter.category')}>
              <Input
                type="search"
                value={draftCategory}
                placeholder={t('notice.filter.categoryPlaceholder')}
                onChange={(e) => setDraftCategory(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button size="sm" onClick={applyFilters}>{t('notice.filter.search')}</Button>
            <Button size="sm" variant="outline" onClick={resetFilters}>{t('notice.filter.reset')}</Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('notice.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('notice.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.noticeId}
          emptyState={t('notice.empty')}
          stickyHeader
        />
      )}

      {pagination ? (
        <Pagination
          total={pagination.totalItems}
          currentPage={applied.page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => setApplied((prev) => ({ ...prev, page: p }))}
          className="mt-4"
        />
      ) : null}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('notice.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('notice.deleteDesc', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('notice.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.noticeId, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('notice.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
